import React from 'react';
import styled from 'styled-components';
import { FaExclamationCircle } from 'react-icons/fa';

import Button from '../../../components/Button/Button';
import Modal from '../../../components/Modal/Modal';
import useScreenQuery from '../../../hooks/useScreenQuery';
import PlayupThemeProvider from '../../../themes/PlayupThemeProvider';
import { TitleHeader } from '../sharedStyledComponents';
import { BodyContainer, PageContainer } from '../sharedStyles';
import { formatCurrency } from '../../../utils/formatCurrency';
import FixedLoader from '../../../components/Loader/FixedLoader';

/*
	DevNote: Components not migrated to storybook and why:
	WithBankAccount: Very complicated to migrate to storybook (react-hook-form)
	NoBankAccount: Very complicated to migrate to storybook (react-hook-form)
	PendingWithdrawals: Require Pagination, which requires react-router-dom most likely won’t work in consumer projects
	VerifyIdLink: <NavLink> created in web-core won't work in consumer project
*/
const PortalWithdrawal = ({
	user,
	withdrawals,
	pagination,
	sending = false,
	loadingWithdrawals = true,
	fetchPendingWithdrawals,
	onCancelWithdrawal,
	onBankAccountRemoved,
	WithBankAccount,
	NoBankAccount,
	PendingWithdrawals,
	ProfileLink,
	VerifyIdLink,
	CashTransactionLink,
}: PortalWithdrawalProps) => {
	const { isSmall } = useScreenQuery();
	const [showModal, setShowModal] = React.useState(false);

	const updateBankAccountDetails = () => {
		onBankAccountRemoved();
		setShowModal(false);
	};
	return (
		<PlayupThemeProvider>
			{showModal && (
				<Modal onClose={() => setShowModal(false)} title="Update Bank Details">
					<ModalText isSmallScreen={isSmall}>If you proceed, all pending withdrawals will be cancelled!</ModalText>
					<Button isLoading={sending} kind="danger" height={50} onClick={updateBankAccountDetails}>
						Confirm
					</Button>
					<Button height={50} onClick={() => setShowModal(false)}>
						Cancel
					</Button>
				</Modal>
			)}
			{isSmall && <TitleHeader title={'Withdraw'} />}
			<PageContainer isSmallScreen={isSmall}>
				{(!user?.id_verified || !user?.phone_verified) && (
					<UserWarning isSmallScreen={isSmall}>
						<WarningIcon isSmallScreen={isSmall} size={20} />
						<WarningText isSmallScreen={isSmall}>
							Please make sure your account and your mobile number are verified to submit a withdrawal.{' '}
							<ProfileLink isSmallScreen={isSmall} />
						</WarningText>
					</UserWarning>
				)}
				<BalanceSection isSmallScreen={isSmall}>
					<BalanceType borderRight={true}>
						<BalanceName>Balance (AUD)</BalanceName>
						<BalanceAmount isSmallScreen={isSmall}>{formatCurrency(user?.balance)}</BalanceAmount>
					</BalanceType>
					<BalanceType>
						<BalanceName>Withdrawal Balance (AUD)</BalanceName>
						<BalanceAmount isSmallScreen={isSmall}>{formatCurrency(user?.available_balance)}</BalanceAmount>
					</BalanceType>
				</BalanceSection>
				<BodyContainer isSmallScreen={isSmall}>
					{user?.id_verified && user?.phone_verified ? (
						<>
							{user?.user_bank_account ? (
								<WithBankAccount
									userBankAccount={user.user_bank_account}
									documentRequired={user?.locale?.bank_document_required}
									onWithdrawalCreated={fetchPendingWithdrawals}
									updateBankAccountClicked={() => setShowModal(true)}
								/>
							) : (
								<NoBankAccount
									documentRequired={user?.locale?.bank_document_required}
									onWithdrawalCreated={fetchPendingWithdrawals}
								/>
							)}
							<HintText isSmallScreen={isSmall}>
								Please allow up to 2 business days for your request to be processed
							</HintText>
							<HintText isSmallScreen={isSmall}>
								You can check your processed withdrawals in <CashTransactionLink isSmallScreen={isSmall} />
							</HintText>
						</>
					) : (
						<VerifyIdLink isSmallScreen={isSmall} />
					)}
				</BodyContainer>
			</PageContainer>
			{loadingWithdrawals ? (
				<FixedLoader />
			) : (
				<ChartContainer isSmallScreen={isSmall}>
					<PendingWithdrawals
						withdrawals={withdrawals}
						pagination={pagination}
						onCancelWithdrawal={onCancelWithdrawal}
					/>
				</ChartContainer>
			)}
		</PlayupThemeProvider>
	);
};

export default PortalWithdrawal;

export interface PortalWithdrawalProps {
	user: PortalUser;
	withdrawals: Array<Withdrawal>;
	pagination: Array<Object>;
	sending: boolean;
	loadingWithdrawals: boolean;
	fetchPendingWithdrawals: () => void;
	onCancelWithdrawal: (withdrawalId: number) => void;
	onBankAccountRemoved: () => void;
	WithBankAccount: React.FC<{
		userBankAccount: any;
		documentRequired?: boolean;
		onWithdrawalCreated: () => void;
		updateBankAccountClicked: () => void;
	}>;
	NoBankAccount: React.FC<{
		documentRequired?: boolean;
		onWithdrawalCreated: () => void;
	}>;
	ProfileLink: React.FC<{ isSmallScreen: boolean }>;
	VerifyIdLink: React.FC<{ isSmallScreen: boolean }>;
	CashTransactionLink: React.FC<{ isSmallScreen: boolean }>;
	PendingWithdrawals: React.FC<{
		withdrawals: Array<Withdrawal>;
		pagination: Array<Object>;
		onCancelWithdrawal: (val: number) => void;
	}>;
}

const ModalText = styled.p<{ isSmallScreen: boolean }>`
	text-align: center;
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '20px' : '30px')};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
`;

const UserWarning = styled.div<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	padding: ${({ isSmallScreen }) => (isSmallScreen ? '10px 14px' : '15px 30px')};
	background: ${({ theme }) => theme.colors.warning};
`;

const WarningIcon = styled(FaExclamationCircle)<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.white};
	margin-right: ${({ isSmallScreen }) => (isSmallScreen ? '6px' : '12px')};
`;

const WarningText = styled.p<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.white};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '12px' : '14px')};
	margin: 0;
`;

const BalanceSection = styled.section<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: row;
	width: 100%;
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '25px' : '30px')};
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '15px')};
`;

const BalanceType = styled.div<{ borderRight?: boolean }>`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	${({ borderRight }) => borderRight && `border-right: 2px solid;`}
	border-color: ${({ theme }) => theme.colors.lightGreyR}
`;

const BalanceName = styled.p`
	color: ${({ theme }) => theme.colors.greyR};
	font-size: 14px;
	text-align: center;
`;

const BalanceAmount = styled.p<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.darkR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '22px' : '30px')};
	font-weight: 600;
	text-align: center;
`;

const HintText = styled.p<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.darkR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '12px' : '16px')};
	text-align: center;
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '7px')};
`;

const ChartContainer = styled.div<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	background-color: ${({ theme }) => theme.colors.darkR};

	${({ isSmallScreen }) => !isSmallScreen && 'max-width: 752px; margin: 30px auto 0;'}
`;
